interface CreditCardBillSummary {
  id: number | null;
  isGenerated: boolean;
  amount: number | null;
  approxAmount: number;
  approxAsOf: string;
  dueDate: string | null;
  paidAmount: number;
  paidOn: string | null;
}
interface PayslipLineItem {
  description: string;
  amount: number;
}
interface InboundEmailAttachment {
  name: string;
  contentType: string;
  size: number;
  s3Path?: string;
  signedUrl?: string;
  error?: string;
}
// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  metadata?: {
    page?: number;
    total?: number;
    limit?: number;
    count?: number;
    offset?: number;
    state?: string;
    name?: string;
  };
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// Account Types - matching backend schema exactly
export enum AccountType {
  CASH = "cash",
  BANK = "bank",
  CREDIT_CARD = "credit_card",
  LOAN = "loan",
  DIGITAL_WALLET = "digital_wallet",
  OTHER = "other",
  CRYPTOCURRENCY = "cryptocurrency",
}

export enum LoanInterestType {
  FIXED = "fixed",
  FLOATING = "floating",
}

export interface Account {
  id: number;
  accountName: string;
  type: AccountType;
  currentBalance: number;
  currencyCode: string;
  accountNumber?: string;
  lastFourAccountNumber?: string;
  previousLastFourAccountNumber?: string[];
  creditLimit?: number;
  availableCreditLimit?: number;
  lastStatementBalance?: number;
  statementDayOfMonth?: number;
  paymentDueDayOfMonth?: number;
  isDeleted: boolean;
  isArchived: boolean;
  slug: string;
  iconUrl?: string;
  interestRate?: number;
  termInMonths?: number;
  paymentStartDate?: string;
  syncWalletAppRecords?: boolean;
  walletAppAccountId?: string;
  syncFoldMoneyRecords?: boolean;
  notes?: string;
  foldMoneyAccountId?: string;
  sortOrder?: number;
  currentBillingCycle?: string;
  lastUpdated?: string;
  loanInterestType?: LoanInterestType;
  bankSyncEnabled?: boolean;
  bankSyncProvider?: BankSyncProvider;
  bankSyncConfig?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// Transaction Types - matching backend schema exactly
export enum TransactionType {
  CREDIT = "credit",
  DEBIT = "debit",
}

// Added By Sources - matching backend schema exactly
export enum AddedBy {
  MANUAL = "manual",
  SMS = "sms",
  EMAIL = "email",
  CSV_IMPORT_JOB = "csv_import_job",
  FOLD_MONEY_SYNC = "fold_money_sync",
  INDMONEY_SYNC = "indmoney_sync",
  WEB_SCRAPING = "web_scraping",
  AI_EXTRACTED = "ai_extracted",
  MOBILE_NOTIFICATION = "mobile_notification",
  TELEGRAM = "telegram",
  BANK_SYNC = "bank_sync",
}

export enum BankSyncProvider {
  FOLD_MONEY = "fold_money",
}

/** An account as returned by the Fold Money provider (live discovery). */
export interface FoldMoneyAvailableAccount {
  id: string;
  name: string;
  type: string;
  balance?: number;
  currency?: string;
  /** Raw provider payload — Fold synthesizes names, so extra identifying
   * detail (if any) only exists here. */
  raw?: Record<string, unknown>;
}

// Payment Modes - matching backend schema exactly
export enum PaymentMode {
  UPI = "upi",
  INTERNET_BANKING = "internet_banking",
  E_MANDATE = "e_mandate",
  CREDIT_CARD = "credit_card",
  DEBIT_CARD = "debit_card",
}

export interface Transaction {
  id: number;
  date: string;
  type: TransactionType;
  amount: number;
  description?: string;
  payee?: string;
  merchantName?: string;
  notes?: string;
  referenceNo?: string;
  accountId: number;
  account?: Account;
  /** `null` when uncategorized (the column is nullable). */
  categoryId?: number | null;
  category?: Category;
  tags?: Tag[];
  addedBy: AddedBy;
  isReviewed: boolean;
  isAutoCategorized?: boolean;
  isIncome?: boolean;
  isManuallyCategorized?: boolean;
  isTransfer?: boolean;
  destinationAccountId?: number;
  linkedTransactionId?: number;
  excludeFromBudget?: boolean;
  excludeFromClassifierTraining?: boolean;
  location?: Record<string, unknown>;
  paymentMode?: PaymentMode;
  currencyCode: string;
  billingCycle?: string;
  rewardAmount?: number;
  tempDescription?: string;
  meta?: Record<string, unknown>;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionFormData {
  id?: number;
  date: string;
  type: TransactionType;
  amount: number;
  description?: string;
  payee?: string;
  merchantName?: string;
  notes?: string;
  referenceNo?: string;
  accountId: number;
  categoryId?: number | null;
  tagIds?: number[];
  isReviewed?: boolean;
  isIncome?: boolean;
  excludeFromBudget?: boolean;
  paymentMode?: PaymentMode;
  currencyCode?: string;
  tempDescription?: string;
  meta?: Record<string, unknown>;
}

// Bulk actions that can be applied to a selection of transactions at once.
// `categoryId: null` explicitly clears the category on all selected transactions.
export interface BulkUpdateTransactionsData {
  categoryId?: number | null;
  isReviewed?: boolean;
}

// Category Types
export interface Category {
  id: number;
  name: string;
  isIncome: boolean;
  isGroup: boolean;
  groupId?: number;
  sortOrder?: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryDTO {
  name: string;
  isIncome?: boolean;
  isGroup?: boolean;
  groupId?: number;
  sortOrder?: number;
}

export interface UpdateCategoryDTO {
  name?: string;
  isIncome?: boolean;
  isGroup?: boolean;
  groupId?: number;
  sortOrder?: number;
}

// Tag Types
export interface Tag {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

// Filter Types
export interface TransactionFilters {
  startDate?: string;
  endDate?: string;
  accountId?: number;
  categoryId?: number;
  type?: TransactionType;
  isReviewed?: boolean;
  minAmount?: number;
  maxAmount?: number;
  notes?: string;
  search?: string;
  billingCycle?: string;
  accountType?: AccountType;
  limit?: number;
  offset?: number;
  sortBy?: "date" | "amount" | "createdAt";
  sortOrder?: "asc" | "desc";
}

export interface TransactionFilterAI {
  startDate?: string;
  endDate?: string;
  accountId?: number;
  accountName?: string;
  categoryId?: number;
  categoryName?: string;
  /** The AI parser can answer "transfer", which the transactions filter does not support. */
  type?: TransactionType | "transfer";
  minAmount?: number;
  maxAmount?: number;
  isReviewed?: boolean;
  searchText?: string;
  explanation: string;
}

// Dashboard Types
export interface DashboardSummary {
  totalBalance: number;
  monthlyIncome: number;
  monthlyExpense: number;
  netSavings: number;
  accountCount: number;
  transactionCount: number;
  accountBreakdown: AccountByType[];
}

export interface AccountByType {
  type: string;
  count: number;
  totalBalance: number;
  accounts: Array<{
    id: number;
    name: string;
    balance: number;
    currencyCode: string;
    type: string;
  }>;
}

export interface MonthlySpendingSummary {
  month: string;
  totalSpent: number;
  totalIncome: number;
  categoriesSpending: Array<{
    categoryName: string;
    /** -1 for the synthetic "Uncategorized" bucket (Q13). */
    categoryId: number;
    isUncategorized: boolean;
    amount: number;
    percentage: number;
    transactionCount: number;
  }>;
  netCashFlow: number;
}

export interface RecentTransaction {
  id: number;
  description: string;
  amount: number;
  type: "credit" | "debit";
  date: string;
  accountName: string;
  categoryName?: string;
  isReviewed: boolean;
}

// Transactional SMS Types
export interface TransactionalSms {
  id: number;
  date: string;
  timestamp: string;
  from: string | null;
  body: string | null;
  location: Record<string, unknown> | null;
  extractedOutput: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  Transaction?: Array<{
    id: number;
    amount: number;
    description: string | null;
    type: TransactionType;
  }>;
}

export interface TransactionalSmsFilters {
  from?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

// Mobile Notification Types
export interface MobileNotification {
  id: number;
  title: string | null;
  text: string | null;
  sub_text: string | null;
  app_name: string | null;
  app_package: string | null;
  text_lines: string | null;
  text_big: string | null;
  action_names: string | null;
  ticker_text: string | null;
  device: string | null;
  createdAt: string;
}

export interface MobileNotificationFilters {
  appPackage?: string;
  device?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

// Inbound Email Types

export interface InboundEmail {
  id: number;
  date: string;
  from: string | null;
  to: string | null;
  subject: string | null;
  body: string | null;
  htmlBody: string | null;
  messageId: string | null;
  attachments: InboundEmailAttachment[] | null;
  meta: Record<string, unknown> | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  transactions?: Array<{
    id: number;
    amount: number;
    description: string | null;
    type: TransactionType;
  }>;
}

export interface InboundEmailFilters {
  from?: string;
  subject?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

// Budget Types
export interface Budget {
  id: number;
  categoryId: number;
  category?: Category;
  month: string; // Format: YYYY-MM (e.g., "2025-01" for January 2025)
  amount: number;
  notes?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BudgetFormData {
  id?: number;
  categoryId: number;
  month: string;
  amount: number;
  notes?: string;
}

export interface BudgetVsActual {
  categoryId: number;
  categoryName: string;
  budgetAmount: number;
  actualSpent: number;
  remaining: number; // budgetAmount - actualSpent
  percentageUsed: number;
  variance: number; // positive = under budget, negative = over budget
  transactionCount: number;
  isOverBudget: boolean;
}

export interface BudgetAnalysis {
  month: string; // YYYY-MM format (e.g., "2025-01")
  monthDisplay: string; // "January 2025"
  totalBudgeted: number;
  totalSpent: number;
  totalRemaining: number; // totalBudgeted - totalSpent
  percentageUsed: number;
  categoriesAnalysis: BudgetVsActual[];
  categoriesOverBudget: number;
  categoriesUnderBudget: number;
  categoriesOnTrack: number; // within 90-100% of budget
}

export interface BudgetFilters {
  categoryId?: number;
  month?: string;
  startMonth?: string;
  endMonth?: string;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
}

// Note Types

export interface Note {
  id: number;
  noteType: "monthly" | "running"; // monthly or running
  month?: string | null; // YYYY-MM format (e.g., "2025-01" for January 2025) - for monthly notes
  slug?: string | null; // Slug identifier for running notes (e.g., "general", "ideas")
  content?: string | null; // Markdown content
  date: string;
  source: "web_app" | "telegram" | "others";
  createdAt: string;
  updatedAt: string;
  meta?: Record<string, unknown> | null;
}

export interface UpsertNoteDTO {
  month: string;
  content?: string;
}

export interface UpsertRunningNoteDTO {
  slug: string;
  content?: string;
}

// Bill Types
export enum BillingCycleType {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
}

export enum BillTrackingMode {
  MANUAL = "manual",
  AUTO = "auto",
}

export enum ReminderType {
  SAME_DAY = "same_day",
  DAYS_BEFORE = "days_before",
}

export interface Bill {
  id: number;
  name: string;
  notes?: string | null;
  amount: number;
  billingDate: string;
  endDate?: string | null;
  billingCycleType: BillingCycleType;
  billingCycleValue: number;
  accountId?: number | null;
  account?: Account | null;
  trackingMode: BillTrackingMode;
  receiveReminders: boolean;
  reminderType: ReminderType;
  reminderDays?: number | null;
  isActive: boolean;
  lastBilledDate?: string | null;
  nextBillingDate: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
}

export interface BillFormData {
  name: string;
  notes?: string;
  amount: number;
  billingDate: string;
  endDate?: string;
  billingCycleType: BillingCycleType;
  billingCycleValue: number;
  accountId?: number | null;
  trackingMode: BillTrackingMode;
  receiveReminders: boolean;
  reminderType: ReminderType;
  reminderDays?: number;
  isActive?: boolean;
  isDeleted?: boolean;
}

export interface BillFilters {
  isActive?: boolean;
  trackingMode?: BillTrackingMode;
  billingCycleType?: BillingCycleType;
  upcomingDays?: number;
  search?: string;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: "name" | "amount" | "nextBillingDate" | "createdAt";
  sortOrder?: "asc" | "desc";
}

export interface BillSummary {
  totalActive: number;
  totalUpcoming30Days: number;
  totalOverdue: number;
  totalMonthlyAmount: number;
  upcomingBills: Bill[];
  overdueBills: Bill[];
}

export interface BillPayment {
  id: number;
  billId: number;
  paidDate: string;
  amount: number;
  accountId?: number | null;
  account?: { id: number; accountName: string } | null;
  bill?: { id: number; name: string; amount: number } | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillPaymentFormData {
  billId: number;
  paidDate: string;
  amount: number;
  accountId?: number;
  notes?: string;
}

export interface BillPaymentStats {
  totalPayments: number;
  totalAmount: number;
  averageAmount: number;
  lastPaymentDate: string | null;
  lastPaymentAmount: number | null;
}

// Payslip Types

export interface Payslip {
  id: number;
  company: string;
  month: string; // YYYY-MM format
  pdfS3Path: string;
  fileName: string;
  fileSize?: number | null;
  grossEarnings?: number | null;
  grossDeductions?: number | null;
  netPay?: number | null;
  notes?: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  earnings?: PayslipLineItem[];
  deductions?: PayslipLineItem[];
}

export interface PayslipFormData {
  company: string;
  month: string;
  pdf: File;
  grossEarnings?: number;
  grossDeductions?: number;
  netPay?: number;
  notes?: string;
  earnings?: Array<{ description: string; amount: number }>;
  deductions?: Array<{ description: string; amount: number }>;
}

export interface BulkUploadPayslipFormData {
  company: string;
  pdfs: File[];
}

export interface BulkUploadPayslipResult {
  successful: number;
  failed: number;
  errors: Array<{ file: string; error: string }>;
}

export interface PayslipFilters {
  company?: string;
  month?: string;
  startMonth?: string;
  endMonth?: string;
  includeDetails?: boolean;
  includeDeleted?: boolean;
}

// Credit Report Types
export interface CreditReport {
  id: number;
  bureau: "cibil" | "experian" | "crif";
  reportDate: string;
  creditScore?: number | null;
  source?: "onescore" | "indmoney" | "others" | null;
  pdfS3Path?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  notes?: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreditReportFormData {
  bureau: "cibil" | "experian" | "crif";
  reportDate: string;
  creditScore?: number;
  source?: "onescore" | "indmoney" | "others";
  pdf?: File;
  notes?: string;
}

export interface CreditReportFilters {
  bureau?: "cibil" | "experian" | "crif";
  startDate?: string;
  endDate?: string;
  includeDeleted?: boolean;
}

// Statement Types
export enum StatementStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum StatementType {
  EXTRACTION = "extraction", // For AI extraction workflow
  ARCHIVAL = "archival", // For bulk uploaded archival statements
}

export interface Statement {
  id: number;
  fileName: string;
  fileS3Path: string;
  fileSize?: number | null;
  accountId?: number | null;
  month?: string | null; // YYYY-MM format for archival statements
  statementType: StatementType;
  notes?: string | null;
  uploadedAt: string;
  status: StatementStatus;
  extractedData?: Record<string, unknown> | null;
  selectionData?: Record<string, unknown> | null;
  jobId?: string | null;
  errorMessage?: string | null;
  transactionsCreated?: number | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  account?: {
    id: number;
    accountName: string;
    slug: string;
  };
}

export interface BulkUploadStatementsFormData {
  accountId: number;
  pdfs: File[];
  notes?: string;
}

export interface BulkUploadStatementsResult {
  uploaded: Statement[];
  failed: Array<{
    fileName: string;
    error: string;
  }>;
}

// AI Model Types
export interface AIModelOption {
  /** The gateway model id sent to the API. */
  id: string;
  /** Human-readable label shown in the dropdown. */
  label: string;
  /** True if the model accepts native PDF file parts. */
  nativeDocuments: boolean;
  /** True if the model accepts image parts. */
  nativeImages: boolean;
  /** Whether this is a recommended/curated model (surfaced first). */
  recommended?: boolean;
}

export interface AIModelListResponse {
  models: AIModelOption[];
  defaultModel: string;
}

// Workflow Types
export type TriggerType = "email_received" | "scheduled" | "manual";

export type ActionType = "unlock_pdf" | "add_as_statement" | "webhook";

export type ConditionOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "greater_than"
  | "less_than"
  | "exists"
  | "not_exists";

export interface ActionCondition {
  field: string;
  operator: ConditionOperator;
  value?: unknown;
}

export interface WorkflowTriggerConfig {
  // Account filtering
  accountId?: number;

  // Scheduled trigger
  cronExpression?: string;

  // Email filtering
  emailFrom?: string;
  emailFromPattern?: string;
  emailSubject?: string;
  emailSubjectContains?: string;
  emailSubjectPattern?: string;
  emailBodyContains?: string;

  // Attachment ignore list (filename patterns; * wildcard or substring,
  // case-insensitive) — applied to email attachments before actions run.
  attachmentIgnoreList?: string[];

  // Amount filtering
  amountMin?: number;
  amountMax?: number;
}

export interface WorkflowTrigger {
  type: TriggerType;
  config: WorkflowTriggerConfig;
}

export interface WorkflowAction {
  type: ActionType;
  config: Record<string, unknown>;
  conditions?: ActionCondition[];
}

export interface Workflow {
  id: number;
  name: string;
  description?: string | null;
  enabled: boolean;
  trigger: WorkflowTrigger;
  actions: WorkflowAction[];
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
}

export interface WorkflowExecution {
  id: number;
  workflowId: number;
  status: "running" | "completed" | "failed";
  startedAt: string;
  completedAt?: string | null;
  error?: string | null;
  context: Record<string, unknown>;
  result?: Record<string, unknown> | null;
  logs?: Array<{
    step: number;
    action: string;
    status: string;
    startedAt: string;
    completedAt?: string;
    error?: string;
    result?: unknown;
  }> | null;
  workflow?: Workflow;
}

export interface CreateWorkflowDTO {
  name: string;
  description?: string;
  enabled?: boolean;
  trigger: WorkflowTrigger;
  actions: WorkflowAction[];
}

export interface UpdateWorkflowDTO {
  name?: string;
  description?: string;
  enabled?: boolean;
  trigger?: WorkflowTrigger;
  actions?: WorkflowAction[];
}

// Receipt types
export type ReceiptLineItemCategory =
  | "gold_item"
  | "making_charges"
  | "stone"
  | "tax"
  | "discount"
  | "other";

export interface ReceiptLineItem {
  description: string;
  category: ReceiptLineItemCategory;
  quantity?: number | null;
  unit?: string | null;
  unitPrice?: number | null;
  amount: number;
  hsnCode?: string | null;
  taxRate?: number | null;
  grossWeight?: number | null;
  netWeight?: number | null;
  purity?: string | null;
}

export interface Receipt {
  id: number;
  fileName: string;
  fileS3Path: string;
  fileSize?: number | null;
  receiptDate?: string | null;
  merchantName?: string | null;
  totalAmount?: number | null;
  currency?: string | null;
  extractedData?: ReceiptExtractionResult | null;
  lineItems?: ReceiptLineItem[] | null;
  notes?: string | null;
  sourceEmailId?: number | null;
  sourceEmail?: {
    id: number;
    subject: string | null;
    from: string | null;
  } | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptExtractionResult {
  merchantName?: string | null;
  merchantAddress?: string | null;
  receiptDate?: string | null;
  receiptNumber?: string | null;
  subtotal?: number | null;
  taxTotal?: number | null;
  discountTotal?: number | null;
  totalAmount?: number | null;
  paymentMethod?: string | null;
  currency?: string | null;
  lineItems: ReceiptLineItem[];
}

export interface UploadAndExtractResult {
  receipt: Receipt;
  extractedData: ReceiptExtractionResult;
}

// Loan Interest Rate History Types
export interface LoanInterestRateHistory {
  id: number;
  accountId: number;
  effectiveDate: string;
  effectiveRate: number;
  repoRate?: number | null;
  rblr?: number | null;
  spread?: number | null;
  crp?: number | null;
  notes?: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoanInterestRateHistoryFormData {
  effectiveDate: string | Date;
  effectiveRate: number;
  repoRate?: number;
  rblr?: number;
  spread?: number;
  crp?: number;
  notes?: string;
}

// Beancount Sync Types
export interface GitMirrorSettings {
  remoteUrl: string;
  branch: string;
  /** True when NF_GITHUB_PAT is set server-side. */
  tokenConfigured: boolean;
}

export interface BeancountSyncConfig {
  ledgerKey: string;
  enabled: boolean;
  deletePolicy: "app-first" | "ledger-first";
  conflictPolicy: "skip" | "app" | "ledger";
  git: GitMirrorSettings | null;
}

export type SyncEntityType = "account" | "category" | "transaction";

export type SyncAction =
  | "push-create"
  | "push-update"
  | "pull-update"
  | "app-create"
  | "app-delete"
  | "ledger-delete"
  | "conflict"
  | "skip";

export interface SyncOp {
  action: SyncAction;
  entityType: SyncEntityType;
  entityId: number;
  directiveId?: string;
  label: string;
  detail?: string;
}

export interface BeancountSyncPlan {
  ops: SyncOp[];
  ledgerKey: string;
  dryRun: boolean;
  summary: {
    pullUpdates: number;
    pushUpdates: number;
    appCreates: number;
    appDeletes: number;
    ledgerDeletes: number;
    conflicts: number;
    skipped: number;
  };
}

export interface BeancountSyncStatus {
  enabled: boolean;
  ledgerKey: string;
  lastSyncedAt: string | null;
  mappedCounts: {
    accounts: number;
    categories: number;
    transactions: number;
  };
}

/* -------------------------------------------------------------------------- */
/* Credit Cards                                                               */
/* -------------------------------------------------------------------------- */

/** Account-status scope for credit-cards views (mirrors Accounts' filter). */
export type CreditCardStatus = "active" | "archived" | "all";

/** A single card benefit tracked in the profile's benefits registry. */
export interface CardBenefit {
  title: string;
  description?: string | null;
  category?: string | null;
  expiryDate?: string | null;
}

/** Card terms + curated usage guidance (1:1 with a credit-card account). */
export interface CreditCardProfile {
  id: number;
  issuer?: string | null;
  network?: string | null;
  annualFee?: number | null;
  joiningFee?: number | null;
  interestRatePct?: number | null;
  interestFreeDays?: number | null;
  rewardRateNote?: string | null;
  bestFor?: string[] | null;
  notRecommendedFor?: string[] | null;
  benefits?: CardBenefit[] | null;
  notes?: string | null;
  updatedAt: string;
}

export interface CreditCardProfileEntry {
  accountId: number;
  account: {
    id: number;
    accountName: string;
    lastFourAccountNumber?: string | null;
    iconUrl?: string | null;
    creditLimit?: number | null;
    availableCreditLimit?: number | null;
    currentBalance?: number | null;
    lastStatementBalance?: number | null;
    statementDayOfMonth?: number | null;
    paymentDueDayOfMonth?: number | null;
    isArchived: boolean;
    sortOrder?: number | null;
  };
  profile: CreditCardProfile | null;
}

export interface CreditCardProfileFormData {
  issuer?: string | null;
  network?: string | null;
  annualFee?: number | null;
  joiningFee?: number | null;
  interestRatePct?: number | null;
  interestFreeDays?: number | null;
  rewardRateNote?: string | null;
  bestFor?: string[] | null;
  notRecommendedFor?: string[] | null;
  benefits?: CardBenefit[] | null;
  notes?: string | null;
}

export interface CreditCardOverviewCard {
  accountId: number;
  accountName: string;
  lastFourAccountNumber?: string | null;
  iconUrl?: string | null;
  isArchived: boolean;
  cycleStart: string;
  cycleEnd: string;
  isCalendarFallback: boolean;
  totalSpend: number;
  /** All non-income, non-transfer credits on the card (cashback + refunds + payments). */
  totalCredits: number;
  /** Transfer-flagged repayment credits (isTransfer=true) in the cycle — they reduce what is owed but are not cashback or refunds. */
  totalRepayments: number;
  transactionCount: number;
  creditLimit?: number | null;
  availableCreditLimit?: number | null;
  currentBalance?: number | null;
  utilizationPct?: number | null;
  bill: CreditCardBillSummary;
}

export interface CreditCardCategorySpend {
  categoryId: number;
  categoryName: string;
  categoryGroupId: number | null;
  categoryGroupName: string | null;
  isUncategorized?: boolean;
  isIncome: boolean;
  amount: number;
  transactionCount: number;
}

export interface CreditCardOverview {
  month: string;
  monthDisplay: string;
  cards: CreditCardOverviewCard[];
  totals: {
    totalSpend: number;
    totalCredits: number;
    totalRepayments: number;
    netSpend: number;
    totalApproxOrBillAmount: number;
    totalPaid: number;
    totalOutstanding: number;
    overallUtilizationPct: number | null;
  };
  categories: CreditCardCategorySpend[];
  previousMonthTotalSpend: number;
  spendDeltaVsPrevious: number | null;
  generatedAt: string;
}

export interface CreditCardBillMonthEntry {
  month: string;
  isGenerated: boolean;
  billId: number | null;
  amount: number | null;
  approxAmount: number;
  approxAsOf: string;
  transactionCount: number;
  dueDate: string | null;
  paidAmount: number;
  paidOn: string | null;
  cycleStart: string;
  cycleEnd: string;
  isCalendarFallback: boolean;
  notes?: string | null;
}

export interface CreditCardBillsMatrix {
  months: string[];
  cards: Array<{
    accountId: number;
    accountName: string;
    lastFourAccountNumber?: string | null;
    iconUrl?: string | null;
    isArchived: boolean;
    statementDayOfMonth?: number | null;
    months: CreditCardBillMonthEntry[];
  }>;
  generatedAt: string;
}

export interface CreditCardBillPaymentFormData {
  paidAmount: number;
  paidOn?: string;
  notes?: string;
}

/** Raw CreditCardBill row (server responses for pay/update endpoints). */
export interface CreditCardBill {
  id: number;
  accountId: number;
  month: string;
  amount?: number | null;
  dueDate?: string | null;
  statementDate?: string | null;
  isGenerated: boolean;
  generatedSource: "manual" | "statement_import" | "sync";
  paidAmount?: number | null;
  paidOn?: string | null;
  notes?: string | null;
}

export interface CreditCardBillUpdateFormData {
  amount?: number | null;
  dueDate?: string | null;
  statementDate?: string | null;
  isGenerated?: boolean;
  notes?: string | null;
}

// ── Transfer detection review queue (issue #213 step 3) ───────────────────

export type TransferReviewStatus = "pending" | "accepted" | "dismissed" | "expired";

/** Sanitized leg shown in the queue UI (no account numbers / raw meta). */
export interface TransferReviewLeg {
  id: number;
  date: string;
  amount: string;
  type: string;
  description: string | null;
  payee: string | null;
  accountName: string;
}

export interface TransferReviewQueueItem {
  id: number;
  transactionId: number;
  counterpartTransactionId: number | null;
  tier: number;
  evidence: {
    tier?: number;
    tierName?: string;
    signal?: string;
    candidateCount?: number;
    windowDays?: number;
    counterAccountTypes?: string[];
    detectedAt?: string;
    [key: string]: unknown;
  };
  status: TransferReviewStatus;
  dismissedReason: string | null;
  createdAt: string;
  expiresAt: string;
  transaction: TransferReviewLeg;
  counterpartTransaction: TransferReviewLeg | null;
}

/* ── Data Health (issue #213 step 4, plan §7) ───────────────────────────── */

export type DataHealthSeverity = "error" | "warning" | "info" | "ok";

export type DataHealthLintRuleId =
  | "transfer-pair-missing-counterpart"
  | "transfer-pair-amount-mismatch"
  | "transfer-pair-same-account"
  | "transfer-link-without-flag"
  | "unpaired-card-repayment"
  | "loan-expense-without-split"
  | "uncategorized-rate-high";

export interface DataHealthLintViolation {
  ruleId: DataHealthLintRuleId;
  severity: "error" | "warning";
  transactionIds: number[];
  accountId?: number;
  month?: string;
  message: string;
}

export interface DataHealthRuleSummary {
  ruleId: DataHealthLintRuleId;
  title: string;
  severity: "error" | "warning";
  description: string;
  suggestedFix: string;
  count: number;
}

export interface DataHealthBalanceCheck {
  accountId: number;
  accountName: string;
  accountType: string;
  currencyCode: string | null;
  isArchived: boolean;
  declaredBalance: number | null;
  asOf: string | null;
  daysSinceAsOf: number | null;
  flowsSinceAsOf: number | null;
  expectedBalance: number | null;
  recordedBalance: number | null;
  delta: number | null;
  status:
    | "match"
    | "mismatch"
    | "stale"
    | "mismatch-and-stale"
    | "no-declared-balance"
    | "no-current-balance"
    | "undated-declared-balance";
  severity: DataHealthSeverity;
  message: string;
  transactionCount: number;
}

export interface DataHealthReport {
  generatedAt: string;
  stats: {
    accountsChecked: number;
    accountsWithDeclaredBalance: number;
    transactionsScanned: number;
    pendingReviewEntries: number;
    errors: number;
    warnings: number;
  };
  balanceChecks: DataHealthBalanceCheck[];
  violations: DataHealthLintViolation[];
  rules: DataHealthRuleSummary[];
}
